import { useFormik } from 'formik';
import React, { Fragment, useEffect } from 'react';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { useNavigate, useParams } from 'react-router-dom';
import { Switch, Textarea } from 'rizzui';
import * as yup from 'yup';
import CustomSelect from '../../components/forms/CustomSelect';
import RequiredInput from '../../components/forms/InputHOC';
import { PopoverPicker } from '../../components/forms/PopoverPicker';
import useProductTypes from '../../hooks/api-calls/useProductTypes';
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import { State } from '../../type/entities/state-type';
import FormFooter from '../_layout/_footers/form-footer';
import { usePrivilege } from '../../components/priviledge/PriviledgeProvider';
import { SUBSIDISED_PRODUCT_TYPE_ID } from '../../utils/contractProductType';

interface CreateFormProps {
    isLoading: boolean;
    submit: Function;
    data?: State | undefined;
}

export const entities = [
    { value: 'contract', label: 'Contrato' },
    { value: 'contract_product', label: 'Producto del contrato' },
    { value: 'contract_invoicing', label: 'Facturación del contrato' },
    { value: 'contract_incidence', label: 'Incidencia del contrato' },
    { value: 'contract_blocked', label: 'Bloqueo del contrato' },
    { value: 'contract_commission', label: 'Comisión del contrato' },
    { value: 'invoice', label: 'Factura' },
    { value: 'expiration', label: 'Vencimiento' },
    { value: 'expiration_management', label: 'Gestión de vencimientos' },
    { value: 'incidence', label: 'Incidencia' },
    { value: 'order', label: 'Pedidos' },
    { value: 'lead', label: 'Renovaciones (Oportunidades)' },
];

const entitiesWithoutDraftStatus = ['contract_product', 'contract_invoicing', 'contract_incidence', 'contract_blocked', 'contract_commission'];

const entitiesWithSubsidisedProductTypeOnly = ['contract_blocked'];

const schema = yup.object({
    name: yup.string().min(1, 'Demasiado corto').max(300, 'Demasiado largo').required('Campo obligatorio'),
    color: yup.string().required('Campo obligatorio').matches(/^#[0-9A-Fa-f]{6}$/, 'Color no válido'),
    entity: yup.string().required('Campo obligatorio'),
    productTypes: yup.array().of(yup.string()).nullable(),
    description: yup.string().nullable(),
    initialStatus: yup.boolean(),
    satisfactoryStatus: yup.boolean(),
    unsatisfactoryStatus: yup.boolean(),
    requiresKoReason: yup.boolean(),
    sendOrderCoursesInformationEmail: yup.boolean(),
});

const StateForm: React.FC<CreateFormProps> = ({ isLoading, submit, data }) => {

    const { id = '', entity = '' } = useParams();
    const navigate = useNavigate();
    const { userCan } = usePrivilege();
    const { getProductTypesList } = useProductTypes({ active: true });
    const mode = data ? 'Editar' : 'Crear';

    const handleState = (state: string) => {
        if (state === 'draftStatus') {
            if (entitiesWithoutDraftStatus.includes(formik.values.entity)) return;
            formik.setFieldValue('draftStatus', !formik.values.draftStatus);
            formik.setFieldValue('initialStatus', false);
            formik.setFieldValue('satisfactoryStatus', false);
            formik.setFieldValue('unsatisfactoryStatus', false);
        } else if (state === 'initialStatus') {
            formik.setFieldValue('initialStatus', !formik.values.initialStatus);
            formik.setFieldValue('draftStatus', false);
            formik.setFieldValue('satisfactoryStatus', false);
            formik.setFieldValue('unsatisfactoryStatus', false);
        } else if (state === 'satisfactoryStatus') {
            formik.setFieldValue('satisfactoryStatus', !formik.values.satisfactoryStatus);
            formik.setFieldValue('draftStatus', false);
            formik.setFieldValue('initialStatus', false);
            formik.setFieldValue('unsatisfactoryStatus', false);
        } else if (state === 'unsatisfactoryStatus') {
            formik.setFieldValue('unsatisfactoryStatus', !formik.values.unsatisfactoryStatus);
            formik.setFieldValue('draftStatus', false);
            formik.setFieldValue('initialStatus', false);
            formik.setFieldValue('satisfactoryStatus', false);
            if (formik.values.unsatisfactoryStatus) formik.setFieldValue('requiresKoReason', false);
        } else if (state === 'requiresKoReason') {
            formik.setFieldValue('requiresKoReason', !formik.values.requiresKoReason);
        } else if (state === 'sendOrderCoursesInformationEmail') {
            formik.setFieldValue('sendOrderCoursesInformationEmail', !formik.values.sendOrderCoursesInformationEmail);
        }
    };

    const formik = useFormik({
        initialValues: {
            statusId: data?.id ?? id,
            name: data?.name ?? '',
            description: data?.description ?? '',
            color: data?.color ?? '#607d8b',
            productTypes: data?.productTypes ? data.productTypes?.map((item: any) => item.productType?.id) : [],
            entity: data?.entity ?? entity,
            draftStatus: data?.draftStatus ?? false,
            initialStatus: data?.initialStatus ?? false,
            satisfactoryStatus: data?.satisfactoryStatus ?? false,
            unsatisfactoryStatus: data?.unsatisfactoryStatus ?? false,
            requiresKoReason: data?.requiresKoReason ?? false,
            sendOrderCoursesInformationEmail: data?.sendOrderCoursesInformationEmail ?? false,
        },
        validationSchema: schema,
        validateOnBlur: false,
        // validateOnChange: false,
        onSubmit: values => { submit(values) },
    });

    useEffect(() => {
        if (entitiesWithoutDraftStatus.includes(formik.values.entity) && formik.values.draftStatus) {
            formik.setFieldValue('draftStatus', false);
        }
    }, [formik.values.entity, formik.values.draftStatus]);

    useEffect(() => {
        if (formik.values.entity !== 'order' && formik.values.sendOrderCoursesInformationEmail) {
            formik.setFieldValue('sendOrderCoursesInformationEmail', false);
        }
    }, [formik.values.entity, formik.values.sendOrderCoursesInformationEmail]);

    useEffect(() => {
        if (!entitiesWithSubsidisedProductTypeOnly.includes(formik.values.entity)) {
            return;
        }

        const currentProductTypes = formik.values.productTypes ?? [];
        if (currentProductTypes.length !== 1 || currentProductTypes[0] !== SUBSIDISED_PRODUCT_TYPE_ID) {
            formik.setFieldValue('productTypes', [SUBSIDISED_PRODUCT_TYPE_ID]);
        }
    }, [formik.values.entity, formik.values.productTypes]);

    const isDraftStatusAllowed = !entitiesWithoutDraftStatus.includes(formik.values.entity);
    const isSubsidisedProductTypeLocked = entitiesWithSubsidisedProductTypeOnly.includes(formik.values.entity);
    const subsidisedProductTypeLabel = getProductTypesList().find(
        (productType: { value: string }) => productType.value === SUBSIDISED_PRODUCT_TYPE_ID
    )?.label ?? 'Curso Bonificado';
    const categoryDescription = isDraftStatusAllowed
        ? 'Puede ser un estado borrador, inicial, satisfactorio o insatisfactorio'
        : 'Puede ser un estado inicial, satisfactorio o insatisfactorio';

    const getContent = () => {
        return (
            <div className="@container">
                <form onSubmit={formik.handleSubmit}>
                    <div className="mb-10 grid gap-7 divide-y-2 divide-dashed divide-gray-200 @2xl:gap-9 @3xl:gap-11 font-medium">
                        <FormGroup
                            title="Información General"
                            description="Datos principales del estado"
                            className='pt-6'
                        >
                            <RequiredInput
                                id='name'
                                label="Nombre"
                                formik={formik}
                            />

                            <div className='flex flex-row flex-wrap gap-3 align-bottom'>
                                <RequiredInput
                                    id='color'
                                    label="Color"
                                    inputClassName="text-sm flex flex-grow"
                                    formik={formik}
                                />
                                <PopoverPicker color={formik.values.color} onChange={(color: string) => formik.setFieldValue('color', color)} />
                            </div>

                            <CustomSelect
                                isSearchable
                                id={'entity'}
                                label="Entidad"
                                value={{ value: formik.values.entity, label: entities?.find((g: any) => g.value === formik.values.entity)?.label }}
                                options={entities}
                                onChange={(e: any) => { 
                                    formik.setFieldValue('entity', e?.value) 
                                    formik.setFieldValue('productTypes', []);
                                }}
                                required
                                error={formik.errors.entity}
                            />

                            <div className="space-y-2">
                                {isSubsidisedProductTypeLocked ? (
                                    <div className="flex flex-col gap-1">
                                        <span className="text-sm font-medium text-gray-900">Tipo de producto</span>
                                        <span className="text-sm text-gray-600">
                                            {subsidisedProductTypeLabel} (único tipo aplicable a estados de bloqueo)
                                        </span>
                                    </div>
                                ) : (
                                    <>
                                        <CustomSelect
                                            isSearchable
                                            isClearable
                                            isMulti
                                            isDisabled={!userCan('get_product_types_for_select', 'product_types')}
                                            id={'productTypeId'}
                                            label="Tipo de Producto"
                                            value={formik.values.productTypes?.map((item: any) => ({ value: item, label: getProductTypesList().find((g: any) => g.value === item)?.label }))}
                                            options={getProductTypesList()}
                                            onChange={(e: any) => {
                                                const values = e?.map((item: any) => item.value);
                                                formik.setFieldValue('productTypes', values);
                                            }}
                                            error={formik.errors.productTypes}
                                        />
                                        {!userCan('get_product_types_for_select', 'product_types') && (
                                            <div className="flex items-center gap-2 text-red-500 text-sm">
                                                <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                                <span>No tienes permisos para ver los tipos de producto</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>

                            <Textarea
                                id='description'
                                label="Descripción"
                                className="[&>label>span]:font-medium col-span-2"
                                rows={3}
                                onChange={formik.handleChange}
                                value={formik.values.description}
                                error={formik.errors.description}
                            />
                        </FormGroup>

                        <FormGroup
                            title="Categoría"
                            description={categoryDescription}
                            className='pt-6'
                        >
                            {isDraftStatusAllowed && (
                                <Switch
                                    id='draftStatus'
                                    label="Estado borrador"
                                    switchKnobClassName='bg-secondary'
                                    className="[&>label>span]:font-medium"
                                    onChange={() => handleState('draftStatus')}
                                    checked={formik.values.draftStatus}
                                />
                            )}

                            <Switch
                                id='initialStatus'
                                label="Estado inicial"
                                switchKnobClassName='bg-secondary'
                                className="[&>label>span]:font-medium"
                                onChange={() => handleState('initialStatus')}
                                checked={formik.values.initialStatus}
                            />

                            <Switch
                                id='satisfactoryStatus'
                                label="Estado satisfactorio"
                                checked={formik.values.satisfactoryStatus}
                                onChange={() => handleState('satisfactoryStatus')}
                                switchKnobClassName='bg-secondary'
                                className="[&>label>span]:font-medium"
                            />

                            <Switch
                                id='unsatisfactoryStatus'
                                label="Estado insatisfactorio"
                                checked={formik.values.unsatisfactoryStatus}
                                onChange={() => handleState('unsatisfactoryStatus')}
                                switchKnobClassName='bg-secondary'
                                className="[&>label>span]:font-medium"
                            />

                            {formik.values.unsatisfactoryStatus && (
                                <Switch
                                    id='requiresKoReason'
                                    label="Requiere motivo de KO"
                                    checked={formik.values.requiresKoReason}
                                    onChange={() => handleState('requiresKoReason')}
                                    switchKnobClassName='bg-secondary'
                                    className="[&>label>span]:font-medium"
                                />
                            )}

                            {formik.values.entity === 'order' && (
                                <Switch
                                    id='sendOrderCoursesInformationEmail'
                                    label="Enviar email info cursos al cambiar a este estado"
                                    checked={formik.values.sendOrderCoursesInformationEmail}
                                    onChange={() => handleState('sendOrderCoursesInformationEmail')}
                                    switchKnobClassName='bg-secondary'
                                    className="[&>label>span]:font-medium"
                                />
                            )}
                        </FormGroup>
                    </div>
                </form>
            </div>
        )
    };

    return (
        <Fragment>
            <Page container="fluid">
                {(mode === "Editar" && data) && getContent()}
                {mode === "Crear" && getContent()}
            </Page>
            <FormFooter
                submitBtnText={mode + ' Estado'}
                handleCancelBtn={() => navigate(-1)}
                handleSubmitBtn={formik.submitForm}
                isLoading={isLoading}
            />
        </Fragment>
    )
};

export default StateForm;
